import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import _ from 'lodash'
import axios from 'axios'
import moment from 'moment'
import validate from 'validate.js'
import DatePicker from 'react-datepicker'

import config from '@/config/config-esm'

import { setFormData } from '@/store/slices/formDataSlice'
import { randStr } from '@/lib/utils'

import Subscriptions from '@/components/MainFlow/AddSubscriptions/AddPhoneItem/Subscriptions'
import Campaigns from '@/components/MainFlow/AddSubscriptions/AddPhoneItem/Campaigns'
import ExtraServices from '@/components/MainFlow/AddSubscriptions/AddPhoneItem/ExtraServices'
import ExtraAbroadCountry from '@/components/MainFlow/AddSubscriptions/AddPhoneItem/ExtraServices/ExtraAbroadCountry'
import OwnerInput from '@/components/OwnerInput'
import DateOfBirthPicker from '@/components/DateOfBirthPicker'
import SpecialCampaign from '@/components/SpecialCampaign'

const operators = [
  {
    id: 1,
    name: 'Telenor',
    allowed: false,
  },
  {
    id: 2,
    name: 'Talkmore',
    allowed: false,
  },
  {
    id: 3,
    name: 'Telia',
    allowed: true,
  },
  {
    id: 4,
    name: 'ICE.net',
    allowed: true,
  },
  {
    id: 5,
    name: 'OneCall',
    allowed: true,
  },
  {
    id: 6,
    name: 'Komplett Mobil',
    allowed: true,
  },
  {
    id: 7,
    name: 'Fjordkraft',
    allowed: true,
  },
  {
    id: 8,
    name: 'Annen operatør',
    allowed: true,
  },
]

const constraints = {
  simNumber: {
    length: {
      is: 12,
    },
  },
  ssn: {
    length: {
      is: 11,
    },
  },
  birthDate: {
    format: {
      pattern: /^([012][0-9]\.[01][0-9]\.[12][0-9][0-9][0-9])$/,
    },
    length: {
      is: 10,
    },
  },
}

const alwaysAddOnServiceIds = [
  '4d03eef1-5404-4a94-a65a-48a49a149ac4',
  'dbddf5ce-e6ab-4096-90e8-673c128cb5fa',
]

const specialCampaignSubIds = [
  'ab7b4d66-8ebc-4212-b3f7-5dcb5a492b11', // Enkeltabonnement 1 GB
  'c396b464-150c-4431-a7cd-4f2b422fd10b', // Enkeltabonnement 2 GB
  '66559ff0-d88e-415a-b053-8060e9329e4e', // Enkeltabonnement 6 GB
  '2eccbf94-7fc3-464b-ad68-48566f990c27', // Enkeltabonnement 8 GB
  'c0b9ce36-552c-427d-8f36-51e7d2c81acb', // Enkeltabonnement 15 GB
  '3e3e14b0-ec9c-433c-9605-b788fc17ba8d', // Enkeltabonnement 25 GB
]

const defaultItem = {
  campaigns: [],
  extraServices: [],
  simMethod: 'auto',
  portingTime: 5,
}

function AddPhoneItemComponent(props) {
  const dispatch = useDispatch()
  const user = useSelector((state: any) => state.user)
  const formData = useSelector((state: any) => state.formData)
  const layout = useSelector((state: any) => state.layout)
  
  const { editIndex, cancel, save, subscriptions } = props
  const { departmentId } = layout.data

  const existingItem =
    typeof editIndex === 'number' ? formData.phoneItems[editIndex] : null
  const dob =
    existingItem && existingItem.dateOfBirth
      ? moment(existingItem.dateOfBirth).format('DD.MM.YYYY')
      : ''
  const tweakedDefaultItem = { ...defaultItem }
  if (departmentId === config.ukraineDepartmentId) {
    tweakedDefaultItem.purchaseMethod = 'new'
    tweakedDefaultItem.subscriptionId = config.ukraineSubscriptionId
    tweakedDefaultItem.verified = true
    tweakedDefaultItem.isOwner = true
    tweakedDefaultItem.campaigns = config.ukraineCampaigns
    tweakedDefaultItem.simMethod = 'input'
  }
  
  const [ownerWantsSubscription, setOwnerWantsSubscription] = useState(true)
  const [showAddOnModal, setShowAddOnModal] = useState(false)
  const [item, setItem] = useState(existingItem || tweakedDefaultItem)
  const [dobState, setDobState] = useState(dob)
  const [extraServices, setExtraServices] = useState([])
  const [choices, setChoices] = useState({})
  const [errors, setErrors] = useState({
    simNumber: false,
  })
  const [valid, setValid] = useState({
    simNumber: false,
  })
  const [simError, setSimError] = useState(false)
  
  useEffect(() => {
    if (item && item.subscriptionId) {
      getSubscriptionsExtraServices(item.subscriptionId)
    }
  }, [])

  const getSubscriptionsExtraServices = (subscriptionId) => {
    // axios.get(`${config.apiUrl}/subscriptions/${subscriptionId}/extra-services?active=true&perPage=50&order=order`)
    axios
      .get(`${config.apiUrl}/subscription-extra-services/${subscriptionId}`)
      .then((r) => {
        // let extraServices = [...r.data]
        // // filter away extra services that should not be allowed for current agency
        // if ( _.has(this.props, 'layout.data.departmentId') ) {
        //   const depId = this.props.layout.data.departmentId
        //   // Only remove if it's not one of these departments
        //   const depsNotChecked = [
        //     '8fd4decf-133e-49fb-b456-f9c0ba65c238', // MTMST
        //     '6e8e995c-5070-4071-a487-09948a761d65', // Adicio
        //     'b05466bf-bdec-4303-a121-de848c6c85a0', // Adiciocanvas
        //     '89139c3e-ae02-4473-9a3d-ceff542c92b7', // AB marketing - added 2019-10-29 by stian
        //     'a859e4a0-14da-44cb-997c-4fa31fdba19c', // Salestek - added 2019-12-10 by stian
        //   ]
        //   if (depsNotChecked.indexOf(depId) === -1) {
        //     // remove selected extra service
        //     extraServices = _.reject(extraServices, {id: '4d03eef1-5404-4a94-a65a-48a49a149ac4'}) // Digital Trygghet
        //   }
        // }
        // this.setState({extraServices: extraServices})
        setExtraServices(r.data)
      })
      .catch((e) => {
        console.log(e)
      })
  }

  const isOwner = () => {
    return editIndex === 0 || !formData.phoneItems.length
  }

  const handleChange = (e, resetVerification = false, forceVerified = false) => {
    const { departmentId } = layout.data
    const isUkraineFlow = departmentId === config.ukraineDepartmentId

    const newItem = {
      ...item,
      verified: isUkraineFlow
        ? true
        : resetVerification
          ? false
          : forceVerified || item.verified,
      [e.target.name]: e.target.value,
    }
    // if (e.target.name === 'phone' && (!item.portPhone || item.portPhone === item.phone)) {
    //   newItem.portPhone = e.target.value
    // }
    setItem(newItem)
  }

  const toggleExtraService = (id, checked, currentES, fromAlwaysAddOn) => {
    currentES = currentES || []
    if (!fromAlwaysAddOn && alwaysAddOnServiceIds.includes(id)) {
      const choicesCopy = { ...choices }
      if (checked) {
        choicesCopy[id] = 1
      } else {
        delete choicesCopy[id]
      }
      setChoices(choicesCopy)
    }
    if (checked) {
      // add extra service
      return [...currentES, id]
    } else {
      return currentES.filter((es) => es !== id)
    }
  }

  const allowSave = (formData) => {
    const { departmentId } = layout.data
    if (doPersonLookupCheck() && doPreCheck() && doOperatorCheck()) {
      if (item.subscriptionId) {
        if (departmentId === config.ukraineDepartmentId) {
          return (
            item.firstName &&
            item.lastName &&
            item.dateOfBirth &&
            item.address &&
            item.postalCode &&
            item.postalArea &&
            item.simNumber
          )
        } else {
          const chosenProduct = _.find(subscriptions, (sub) => {
            return sub.id === item.subscriptionId
          })
          if (
            (item.extraServices.indexOf('Ekstra Utland') !== -1 && !item.extraAbroadId) ||
            (chosenProduct.kidSafe === true && (!item.childsName || !item.childsDateOfBirth))
          ) {
            return false
          } else {
            if (item.purchaseMethod === 'port' && !item.phone) {
              return false
            } else {
              return true
            }
          }
        }
      }
    }
    return false
  }

  const showAddOnModalFunc = () => {
    const { departmentId } = layout.data

    if (departmentId === config.ukraineDepartmentId) {
      addItem()
      return
    }
    // Use the filtered list instead of the list of IDs, in case some of them are missing
    const alwaysAddonServices = extraServices.filter((e) => alwaysAddOnServiceIds.includes(e.id))
    // If they are all set in the choices object at this point, it means they are all YES,
    // so we can skip the Always Add-On modal
    const newChoices = {}
    alwaysAddonServices.forEach((s) => {
      if (item.extraServices.includes(s.id)) {
        newChoices[s.id] = 1
      }
    })

    setChoices(newChoices)
    if (Object.keys(newChoices).length === alwaysAddonServices.length) {
      addItem()
    } else {
      setShowAddOnModal(true)
    }
  }

  const toggleChoice = (id, value) => {
    const newChoices = { ...choices, [id]: value }
    setChoices(newChoices)
    const newExtraServices = toggleExtraService(id, value, item.extraServices, true)
    const newItem = { ...item, extraServices: newExtraServices }
    setItem(newItem)
  }

  const changeSubscription = (subscriptionId) => {
    // dispatch(setFormData({product: subscriptionId, campaigns: [], extraServices: []}))
    getSubscriptionsExtraServices(subscriptionId)
    const newItem = { ...item, subscriptionId, campaigns: [], extraServices: [] }
    setItem(newItem)
    setChoices({})
  }

  const addItem = () => {
    const { onClose } = props
    const newItem = { ...item, _id: randStr(16) }
    let phoneItems
    if (typeof editIndex === 'number') {
      phoneItems = formData.phoneItems
        .slice(0, editIndex)
        .concat([newItem])
        .concat(formData.phoneItems.slice(editIndex + 1))
    } else {
      phoneItems = formData.phoneItems.concat([newItem])
    }
    dispatch(setFormData({ phoneItems, newPhoneSession: false }))
    onClose()
  }

  const close = () => {
    const { onClose } = props
    dispatch(setFormData({ newPhoneSession: false }))
    onClose()
  }

  const doPersonLookupCheck = () => {
    const { phoneItems } = formData
    return Boolean(item.verified || phoneItems.length)
  }

  const doOperatorCheck = () => {
    if (item.purchaseMethod === 'new') {
      return true
    } else {
      const { operatorId } = item
      if (operatorId) {
        const operator = operators.find((o) => o.id === operatorId)
        if (operator && operator.allowed) {
          return true
        }
      }
      return false
    }
  }

  const doPreCheck = () => {
    if (item.purchaseMethod === 'new') {
      return true
    } else if (item.purchaseMethod === 'port') {
      return doOperatorCheck()
    }
    return false
  }

  const _render_debugInfo = () => {

    return (
      <div className="debug_element">
        <h2>Item</h2>
        <pre>{JSON.stringify(item, null, 2)}</pre>
      </div>
    )
  }

  const _render_addOnModal = () => {
    const alwaysAddonServices = extraServices.filter((e) => alwaysAddOnServiceIds.includes(e.id))

    return (
      <div>
        <div>
          <h3>Sjekkliste Tilleggstjenester</h3>
          <p>Husk å informere kunden om Talkmore sine gode tilleggstjenester.</p>
        </div>
        <div className="toggles">
          {alwaysAddonServices.map((service) => (
            <div
              key={service.id}
              className={`${Object.keys(choices).includes(service.id) ? 'ok' : ''}`}
            >
              <div>
                <div>{service.name}</div>
                <div className="toggle-buttons">
                  <div
                    className={`oh-yeah ${choices[service.id] === 1 ? 'active' : ''}`}
                    onClick={(e) => toggleChoice(service.id, 1)}
                  >
                    Ja takk!
                  </div>
                  <div
                    className={`oh-noes ${choices[service.id] === 0 ? 'active' : ''}`}
                    onClick={(e) => toggleChoice(service.id, 0)}
                  >
                    Nei takk
                  </div>
                </div>
              </div>
              <ExtraAbroadCountry item={item} extraService={service} />
            </div>
          ))}
        </div>
        <div className="buttons">
          <button
            className="cancel"
            onClick={() => { setChoices({}); setShowAddOnModal(false); }}
          >
            Tilbake
          </button>
          <button
            className="continue"
            disabled={Object.keys(choices).length !== alwaysAddonServices.length}
            onClick={addItem}
          >
            Fortsett
          </button>
        </div>
      </div>
    )
  }

  const _render_newOrPort = () => {

    return (
      <React.Fragment>
        <h1>Nytt nummer eller portere?</h1>
        <div className="input_wrapper">
          <input
            type="radio"
            value={true}
            id="new-number"
            checked={item.purchaseMethod === 'new'}
            onClick={(e) => {
              setItem({
                ...item,
                purchaseMethod: 'new',
                phone: '',
                portingDate: null,
                simNumber: '',
              })
            }}
            onChange={(e) => {}}
          />
          <label htmlFor="new-number">
            <span>Ønsker nytt telefonnr</span>
          </label>
        </div>

        <div className="input_wrapper">
          <input
            type="radio"
            value={false}
            id="port-number"
            checked={item.purchaseMethod === 'port'}
            onClick={(e) => {
              setItem({ ...item, purchaseMethod: 'port' })
            }}
            onChange={(e) => {}}
          />
          <label htmlFor="port-number">
            <span>Portere eksisterende telefonnr</span>
          </label>
        </div>
      </React.Fragment>
    )
  }

  const _render_selectOperator = () => {
    let operator
    const { operatorId } = item
    if (operatorId) {
      operator = operators.find((o) => o.id === operatorId)
    }

    return (
      <div>
        <h1>Hvilken operatør har du i dag?</h1>
        {operators.map((opr) => (
          <div className="input_wrapper" key={opr.id}>
            <input
              type="radio"
              name="operator"
              value={opr.id}
              id={`operator-${opr.name}`}
              checked={item.operatorId === opr.id}
              onChange={(e) => {
                setItem({ ...item, operatorId: parseInt(opr.id, 10) })
              }}
            />
            <label htmlFor={`operator-${opr.name}`}>
              <span>{opr.name}</span>
            </label>
          </div>
        ))}
        {operator && !operator.allowed && (
          <div>
            <h2>Vi overfører normalt ikke eksisterende Talkmore eller Telenor-kunder</h2>
            <p>Eventuelle unntak må godkjennes av teamleder.</p>
          </div>
        )}
      </div>
    )
  }

  const _render_subscription = () => {
    return (
      <Subscriptions
        item={item}
        subscriptions={subscriptions}
        changeSubscription={changeSubscription}
      />
    )
  }

  const _render_campaigns = () => {
    return <Campaigns 
      item={item} 
      subscriptionId={item.subscriptionId}
      onItemChange={(newItem) => setItem(newItem)}
    />
  }

  const _render_portPhone = () => {
    return (
      <div className="input_wrapper">
        <label htmlFor="portPhone">Telefonnr.:</label>
        <input
          type="phone"
          id="portPhone"
          value={item.portPhone || ''}
          onChange={(e) => {
            setItem({ ...item, portPhone: e.target.value })
          }}
        />
      </div>
    )
  }

  const _render_portDate = () => {
    const baseDate = moment().hour(12).date(14)
    const validDateIntervals = (item.campaigns || []).includes(config.portDiscountId)
      ? [
          { start: baseDate.toDate(), end: moment(baseDate).date(20).toDate() },
          {
            start: moment(baseDate).add(1, 'month').toDate(),
            end: moment(baseDate).add(1, 'month').date(20).toDate(),
          },
          {
            start: moment(baseDate).add(2, 'month').toDate(),
            end: moment(baseDate).add(2, 'month').date(20).toDate(),
          },
          {
            start: moment(baseDate).add(3, 'month').toDate(),
            end: moment(baseDate).add(3, 'month').date(20).toDate(),
          },
        ]
      : null

    return (
      <div>
        <div className="header_wrapper">
          <h1>Porteringstidspunkt</h1>
          <p>
            Dersom du ikke velger porteringstidspunkt, er det standard porteringstid som er
            gjeldende (6 virkedager frem, kl. 0500).
          </p>
        </div>
        <div className="port-time-wrapper">
          <div>
            <div className="column-heading">Dato</div>
            <div className="input_wrapper">
              <DatePicker
                dateFormat="dd/MM/yyyy"
                className="form-control"
                selected={item.portingDate ? moment(item.portingDate).toDate() : null}
                minDate={
                  valid.simNumber
                    ? formData.personLookupDeferred
                      ? moment().add(6, 'days').toDate()
                      : moment().toDate()
                    : moment().add(7, 'days').toDate()
                }
                includeDateIntervals={validDateIntervals}
                onChange={(date) => {
                  console.log(date)
                  setItem({
                    ...item,
                    portingDate: date
                      ? moment.utc(moment(date).startOf('day') + (86400 * 1000) / 2)
                      : null,
                  })
                }}
              />
            </div>
          </div>
          <div>
            <div className="column-heading">Tidspunkt</div>
            <div className="input_wrapper">
              <select
                className="port-time-selector"
                disabled={!Boolean(item.portingDate)}
                value={item.portingDate ? (item.portingTime || 5).toString() : 5}
                onChange={(ev) => {
                  const portTime = ev.target.value
                  console.log(portTime)
                  setItem({ ...item, portingTime: parseInt(portTime) })
                }}
              >
                <option value="5">05:00</option>
                <option value="6">06:00</option>
                <option value="7">07:00</option>
                <option value="8">08:00</option>
                <option value="9">09:00</option>
                <option value="10">10:00</option>
                <option value="11">11:00</option>
                <option value="12">12:00</option>
                <option value="13">13:00</option>
                <option value="14">14:00</option>
                <option value="15">15:00</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const _render_familyMemberDetails = () => {

    return (
      <div style={{ marginBottom: '1rem' }}>
        <h1>Personalia for familiemedlem</h1>
        <div className="input_wrapper">
          <label htmlFor="firstName">Fornavn:</label>
          <input
            type="text"
            id="firstName"
            value={item.firstName || ''}
            onChange={(e) => {
              setItem({ ...item, firstName: e.target.value })
            }}
          />
        </div>
        <div className="input_wrapper">
          <label htmlFor="lastName">Etternavn:</label>
          <input
            type="text"
            id="lastName"
            value={item.lastName || ''}
            onChange={(e) => {
              setItem({ ...item, lastName: e.target.value })
            }}
          />
        </div>
        <div className="input_wrapper">
          <label htmlFor="phone">Telefonnr.:</label>
          <input
            type="text"
            id="phone"
            value={item.phone || ''}
            onChange={(e) => {
              setItem({ ...item, phone: e.target.value })
            }}
          />
        </div>
        <div className="input_wrapper">
          <label htmlFor="email">Epost:</label>
          <input
            type="text"
            id="email"
            value={item.email || ''}
            onChange={(e) => {
              setItem({ ...item, email: e.target.value })
            }}
          />
        </div>

        <DateOfBirthPicker
          error={errors && errors.dateOfBirth}
          valid={valid && valid.dateOfBirth}
          selected={item.dateOfBirth}
          onChange={(value) => setItem({ ...item, dateOfBirth: value })}
        />

        <div style={{ marginTop: '2rem' }}>
          <h1>Kjønn</h1>
          <div className="input_wrapper">
            <input
              type="radio"
              value={true}
              id="gender-o"
              checked={['M', 'F'].indexOf(item.gender) === -1}
              onClick={(e) => setItem({ ...item, gender: '' })}
              onChange={(e) => {}}
            />
            <label htmlFor="gender-o">
              <span>Ikke spesifisert</span>
            </label>
          </div>

          <div className="input_wrapper">
            <input
              type="radio"
              value={true}
              id="gender-f"
              checked={item.gender === 'F'}
              onClick={(e) => setItem({ ...item, gender: 'F' })}
              onChange={(e) => {}}
            />
            <label htmlFor="gender-f">
              <span>Kvinne</span>
            </label>
          </div>

          <div className="input_wrapper">
            <input
              type="radio"
              value={true}
              id="gender-m"
              checked={item.gender === 'M'}
              onClick={(e) => setItem({ ...item, gender: 'M' })}
              onChange={(e) => {}}
            />
            <label htmlFor="gender-m">
              <span>Mann</span>
            </label>
          </div>
        </div>
      </div>
    )
  }

  const _render_selectSimMethod = () => {
    const { departmentId } = layout.data
    const { chatDepartmentIds } = config

    return (
      <div style={{ marginTop: '2rem' }}>
        <h1>SIM-nummer</h1>
        {/*!formData.phone && <div>Nb: Sim nummer kan kun fylles ut om tlf.nr er fyllt ut</div>*/}
        <div className="input_wrapper">
          <input
            type="radio"
            value={true}
            id="sim-auto"
            checked={item.simMethod === 'auto'}
            onClick={() => {
              setItem({ ...item, simMethod: 'auto', simNumber: '' })
            }}
            onChange={(e) => {}}
          />
          <label htmlFor="sim-auto">
            <span>Talkmore sender nytt SIM-kort i posten</span>
          </label>
        </div>

        {chatDepartmentIds.includes(departmentId) && (
          <div className="input_wrapper">
            <input
              type="radio"
              value={true}
              id="sim-pickup"
              checked={item.simMethod === 'pickup'}
              onClick={() => {
                setItem({ ...item, simMethod: 'pickup', simNumber: 'hentesim' })
              }}
              onChange={(e) => {}}
            />
            <label htmlFor="sim-pickup">
              <span>Kunden henter SIM-kort selv</span>
            </label>
          </div>
        )}

        <div className="input_wrapper">
          <input
            type="radio"
            value={true}
            id="sim-input"
            checked={item.simMethod === 'input'}
            onClick={() => {
              setItem({ ...item, simMethod: 'input', simNumber: '' })
            }}
            onChange={(e) => {}}
          />
          <label htmlFor="sim-input">
            <span>Skriv inn SIM-nummer som skal brukes</span>
          </label>
        </div>
        {item.simMethod === 'input' && (
          <span>
            SIM-nummer må være 12 tegn langt. Lar du feltet stå tomt sendes nytt SIM-kort i posten.
          </span>
        )}
      </div>
    )
  }

  const _render_simNumberInput = () => {
    return (
      <div
        className={`input_wrapper ${errors && errors.simNumber ? 'error' : ''} ${valid && valid.simNumber ? 'validated' : ''}`}
      >
        <input
          type="text"
          /*disabled={formData.phone ? '': 'disabled'}*/
          placeholder="Fyll inn SIM-nummer"
          value={item.simNumber || ''}
          onChange={(e) => {
            const value = e.target.value
            setItem({ ...item, simNumber: value })
          }}
          onBlur={(e) => {
            setErrors(validate({ simNumber: item.simNumber }, constraints))
          }}
          onKeyUp={(e) => {
            const value = e.target.value

            if (value.length === 12) {
              setValid({ simNumber: true })
              setErrors(validate({ simNumber: item.simNumber }, constraints))
            } else {
              setValid({ simNumber: true })
              setErrors(validate({ simNumber: item.simNumber }, constraints))
            }

            setItem({ ...item, simNumber: value })
          }}
        />
      </div>
    )
  }

  const _render_recruitedBy = () => {
    return (
      <div className="input_wrapper">
        <label htmlFor="sim-pickup">
          <span>Rekruttert av</span>
        </label>
        <input
          type="text"
          id="recruited-by"
          value={item.recruitedBy || ''}
          onChange={(e) => setItem({ ...item, recruitedBy: e.target.value })}
        />
      </div>
    )
  }

  const _render_ownerInput = () => {
    return (
      <React.Fragment>
        <h1>Personalia</h1>
        <OwnerInput
          person={item}
          mode="single"
          handleChange={handleChange}
          hidePortType={true}
          ownerWantsSubscription={ownerWantsSubscription}
          onOwnerWantsSubscriptionChange={(value) => setOwnerWantsSubscription(value)}
          onPersonUpdate={(updatedPerson) => setItem(updatedPerson)}
        />
      </React.Fragment>
    )
  }

  const _render_extraServices = () => {
    return (
      <ExtraServices
        item={item}
        extraServices={extraServices}
        toggleExtraService={toggleExtraService}
        onItemChange={(newItem) => setItem(newItem)}
      />
    )
  }

  const _render_buttons = () => {
    return (
      <div className="buttons">
        <button className="cancel" onClick={close}>
          Avbryt
        </button>
        <button className="continue" onClick={showAddOnModalFunc} disabled={!allowSave()}>
          Lagre
        </button>
      </div>
    )
  }

  const _render_blackFriday = () => {
    if (specialCampaignSubIds.includes(item.subscriptionId)) {
      return <SpecialCampaign mode="single" />
    } else {
      return false
    }
  }

  const renderFragmentMaybe = (fragment) => {
    if (!showFragment(fragment)) return false
    const renderMethods = {
      debugInfo: _render_debugInfo,
      addOnModal: _render_addOnModal,
      newOrPort: _render_newOrPort,
      selectOperator: _render_selectOperator,
      subscription: _render_subscription,
      campaigns: _render_campaigns,
      portPhone: _render_portPhone,
      portDate: _render_portDate,
      familyMemberDetails: _render_familyMemberDetails,
      selectSimMethod: _render_selectSimMethod,
      simNumberInput: _render_simNumberInput,
      recruitedBy: _render_recruitedBy,
      ownerInput: _render_ownerInput,
      extraServices: _render_extraServices,
      buttons: _render_buttons,
      blackFriday: _render_blackFriday,
    }
    return renderMethods[fragment] ? renderMethods[fragment]() : null
  }

  const showFragment = (fragment) => {
    const { phoneItems } = formData
    const { departmentId } = layout.data
    const { chatDepartmentIds } = config
    const { purchaseMethod, simMethod } = item
    const personLookupOK = doPersonLookupCheck()
    const preCheckOK = doPreCheck()
    const operatorCheckOK = doOperatorCheck()
    const fullCheckOK = personLookupOK && preCheckOK && operatorCheckOK

    if (showAddOnModal && fragment === 'addOnModal') {
      return true
    }

    if (!showAddOnModal) {
      switch (fragment) {
        case 'newOrPort':
        case 'blackFriday':
          return true
        case 'buttons':
          return true
        case 'debugInfo':
          return config.showingDebugInfo
        case 'selectOperator':
          return purchaseMethod === 'port'
        // case 'portPhone':
        //   return operatorCheckOK && purchaseMethod === 'port'
        case 'subscription':
          return operatorCheckOK
        case 'campaigns':
          // return operatorCheckOK && item.portPhone && item.subscriptionId
          return operatorCheckOK && purchaseMethod === 'port' && item.subscriptionId
        case 'ownerInput':
          return (
            departmentId === config.ukraineDepartmentId ||
            (operatorCheckOK && item.subscriptionId && !phoneItems.length)
          )
        case 'portDate':
          return operatorCheckOK && purchaseMethod === 'port' && item.subscriptionId
        case 'familyMemberDetails':
          return fullCheckOK && !isOwner() && item.subscriptionId
        case 'selectSimMethod':
          return fullCheckOK && purchaseMethod === 'port'
        case 'simNumberInput':
          return fullCheckOK && purchaseMethod === 'port' && simMethod === 'input'
        case 'extraServices':
          return fullCheckOK
        case 'recruitedBy':
          return fullCheckOK && chatDepartmentIds.includes(departmentId)
        default:
          return false
      }
    }
  }

  const orderedFragments = () => {
    // const { purchaseMethod } = item
    const { departmentId } = layout.data

    if (departmentId === config.ukraineDepartmentId) {
      return ['debugInfo', 'ownerInput', 'buttons']
    } else {
      const pre = ['addOnModal', 'debugInfo', 'newOrPort']
      const variable = [
        'selectOperator',
        // 'portPhone',
        'subscription',
        // 'blackFriday',
        'campaigns',
        'ownerInput',
        'familyMemberDetails',
        'portDate',
        'selectSimMethod',
        'simNumberInput',
        'extraServices',
      ]
      const post = ['recruitedBy', 'buttons']

      return pre.concat(variable).concat(post)
    }

    // return [
    //   'addOnModal',
    //   'debugInfo',
    //   'newOrPort',
    //   'selectOperator',
    //   'subscriptionAndCampaigns',
    //   'ownerInput',
    //   'portDate',
    //   'familyMemberDetails',
    //   'selectSimMethod',
    //   'simNumberInput',
    //   'extraServices',
    //   'recruitedBy',
    //   'buttons',
    // ]
  }

  // Main render
  if (!subscriptions.length) return <div>Ingen abonnementer funnet, kan ikke fortsette.</div>

  return (
    <div>{orderedFragments().map((fragment) => (
      <React.Fragment key={fragment}>
        {renderFragmentMaybe(fragment)}
      </React.Fragment>
    ))}</div>
  )
}

const AddPhoneItem = AddPhoneItemComponent

export default AddPhoneItem
